import { useEffect, useState, type ReactElement } from "react";
import { Card } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import "../style.less";
import { useCreateProtocolForm } from "..";
import seedsIcon from "../../../../src/assets/images/planting-blue.svg";
import seedsIconDisabled from "../../../../src/assets/images/planting-gray.svg";
import fertilizerIcon from "../../../../src/assets/images/fertilizer-blue.svg";
import fertilizerIconDisabled from "../../../../src/assets/images/fertilizer-gray.svg";
import biologicalsIcon from "../../../../src/assets/images/biological-gray.svg";
import biologicalsIconDisabled from "../../../../src/assets/images/biological-blue.svg";
import LabelTag from "../../../shared/components/LabelTag";
import { useSearchParams } from "react-router-dom";
import AxiosInstance from "../../../shared/utils/axios";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import { Loader } from "../../../shared/components/Loader";
import { toast } from "react-toastify";
import { getProtocolTypes } from "../utils";
import { useProtocolsRouter } from "../../../shared/custom-hooks/useProtocolsRouter";
import { useTrackingEvent } from "../../../shared/custom-hooks/useTracking";
import { ProtocolCreationAmplitudeEvent } from "../../../shared/utils/amplitudeEvents";

type trialTypes = string | null;

interface TrialTypeCardTypes {
  disabled?: boolean;
  selected?: boolean;
  icon?: any;
  iconDisabled?: any;
  heading: any;
  description: string;
  trialType: trialTypes;
  t?: any;
}

interface TrialTypesComponentProps {
  templateData: any;
}

const getProtocolTypeEventLabel = (selectedType: trialTypes): string => {
  if (selectedType === "seed") return "Seed";
  if (selectedType === "biological") return "CP & Biologicals";
  if (selectedType === "nutrient") return "Nutrient";
  return "";
};

const TrialTypesComponent = ({
  templateData,
}: TrialTypesComponentProps): ReactElement => {
  const { t } = useTranslation();
  const { form, formValidationData, setFormValidationData } =
    useCreateProtocolForm();
  const trackProtocolEvents = useTrackingEvent();
  const trialTypesData = [
    {
      disabled: false,
      icon: seedsIcon,
      iconDisabled: seedsIconDisabled,
      heading: formatTranslation(t("trialtypes.label.seed")),
      description: formatTranslation(t("trialtypes.description.seed")),
      trialType: "seed",
    },
    {
      disabled: false,
      icon: biologicalsIcon,
      iconDisabled: biologicalsIconDisabled,
      heading: formatTranslation(t("trialtypes.label.cropProtection")),
      description: formatTranslation(
        t("trialtypes.description.cropProtection")
      ),
      trialType: "biological",
    },
    {
      disabled: false,
      icon: fertilizerIcon,
      iconDisabled: fertilizerIconDisabled,
      heading: formatTranslation(t("trialtypes.label.fertiliser")),
      description: formatTranslation(t("trialtypes.description.fertiliser")),
      trialType: "nutrient",
    },
  ];

  const [searchParams] = useSearchParams();
  const protocolId = searchParams.get("protocol_id");
  const protocolTypeFromQuery = searchParams.get("protocolType");
  const selectedTemplateName = searchParams.get("template_name");
  const isFromTemplate = Boolean(searchParams.get("template_id"));
  const [existingData, setExistingData] = useState<any>(null);
  const [selectedType, setSelectedType] = useState<trialTypes[]>([]);
  const [originalType, setOriginalType] = useState<trialTypes[]>([]);
  const [showChangeTypeAlert, setShowChangeTypeAlert] = useState(false);

  const [loading, setLoading] = useState<boolean>(false);

  const [error, setError] = useState<boolean>(false);
  const { workspaceId, orgId } = useProtocolsRouter();

  const getProtocolData = async (): Promise<any> => {
    setLoading(true);
    setError(false);
    try {
      const response = await AxiosInstance.get(
        `/protocol/${protocolId as string}`,
        {
          headers: {
            "org-id": orgId ?? "",
            "workspace-id": workspaceId ?? "",
          },
        }
      );
      setExistingData(response.data);
    } catch (error) {
      setError(true);
      toast.error(formatTranslation(t("createProtocol.fetchById.error")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (protocolId) {
      getProtocolData().catch((error) => {
        console.error("Error fetching data:", error);
      });
    } else {
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        isNewProtocol: true,
      }));
    }
  }, [protocolId]);

  /**
   * A protocol that hasn't been created yet carries its selected types only in
   * the query string, so returning to this step (or refreshing on it) has
   * nothing to re-fetch: restore the selection from the URL. The card highlight
   * and the step's Next button both read `formValidationData.protocolType`, so
   * setting the form value alone (done by the parent) isn't enough.
   *
   * Skipped once there is a protocol / template to load from -- the effects
   * below own the selection in those flows.
   */
  useEffect(() => {
    if (protocolId !== null || templateData) return;

    const typesFromQuery = getProtocolTypes(protocolTypeFromQuery);
    if (typesFromQuery.length === 0) return;

    setSelectedType(typesFromQuery);
    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      protocolType: typesFromQuery,
    }));
  }, [protocolTypeFromQuery, protocolId, templateData, setFormValidationData]);

  /**
   * set selected trial types from template data if available,
   * else set selected trial types from existing data if available
   * set template name if available, else set selected trial types from existing data if available
   */
  useEffect(() => {
    if (templateData) {
      const types = getProtocolTypes(templateData?.protocol_type);
      setSelectedType(types);
      setOriginalType(types);
      form.setFieldsValue({
        protocolType: types,
      });
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        protocolType: types,
      }));
    }
  }, [form, setFormValidationData, templateData]);

  useEffect(() => {
    if (existingData) {
      const types = getProtocolTypes(existingData?.protocol_type);

      setSelectedType(types);
      setOriginalType(types);
      form.setFieldsValue({
        protocolType: types,
      });
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        protocolType: types,
        isNewProtocol: false,
      }));
    }
  }, [existingData, form, setFormValidationData]);

  const hasRemovedOriginalType = (
    current: trialTypes[],
    original: trialTypes[]
  ): boolean => original.some((type) => !current.includes(type));

  const handleSelection = (selectedTypeValue: trialTypes): void => {
    const isSelected = selectedType.includes(selectedTypeValue);
    const newSelected = isSelected
      ? selectedType.filter((type) => type !== selectedTypeValue)
      : [...selectedType, selectedTypeValue];
    setSelectedType(newSelected);
    if (!isSelected && selectedTypeValue) {
      void trackProtocolEvents({
        event: ProtocolCreationAmplitudeEvent.PROTOCOL_TYPE_SELECTED,
        protocol_id: protocolId ?? undefined,
        crop_type:
          form.getFieldValue("protocolCrop") ??
          existingData?.crop ??
          templateData?.crop,
        protocol_type: getProtocolTypeEventLabel(selectedTypeValue),
      });
    }
    form.setFieldsValue({ protocolType: newSelected });
    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      protocolType: newSelected,
    }));
    if (isFromTemplate) {
      setShowChangeTypeAlert(hasRemovedOriginalType(newSelected, originalType));
    }
  };

  return (
    <div className="trialTypes-component">
      {loading && <Loader mask message="" />}
      <div className="trial-types-header">
        <h2>{formatTranslation(t("createProtocol.trialType.title"))}</h2>
        {isFromTemplate ? (
          <>
            <div className="trial-types-template-display">
              <span className="trial-types-template-label">
                {formatTranslation(
                  t("createProtocol.templateModal.defaultTemplateProtocol")
                )}
              </span>
              <span className="trial-types-template-value">
                {selectedTemplateName}
              </span>
            </div>
            <p className="trial-types-template-note">
              {formatTranslation(t("trialtypes.templateLoadedFieldsEditable"))}
            </p>
          </>
        ) : (
          <p>
            {formatTranslation(t("createProtocol.trialType.description"))
              .split("\n")
              .map((line, index) => (
                <span key={index}>
                  {line}
                  <br />
                </span>
              ))}
          </p>
        )}
      </div>
      <div className="trialTypes-cards-container">
        {trialTypesData.map((cardData: TrialTypeCardTypes, index: number) => (
          <div
            className="trialTypes-card-content"
            key={index}
            onClick={() => {
              !cardData.disabled && handleSelection(cardData.trialType);
            }}
            onKeyDown={(e) => {
              e.key === "Enter" && handleSelection(cardData.trialType);
            }}
            role="button"
          >
            <TrialTypeCard
              disabled={!!cardData.disabled || !!error}
              icon={cardData.icon}
              iconDisabled={cardData.iconDisabled}
              description={cardData.description}
              heading={cardData.heading}
              selected={
                (Array.isArray(formValidationData?.protocolType) &&
                  formValidationData?.protocolType.includes(
                    cardData.trialType
                  )) ||
                selectedType.includes(cardData.trialType)
              }
              trialType={cardData.trialType}
              t={t}
            />
          </div>
        ))}
      </div>{" "}
      {showChangeTypeAlert && (
        <div className="trial-type-change-alert">
          <div className="trial-type-change-alert__header">
            <WarningOutlined className="trial-type-change-alert__icon" />
            <span className="trial-type-change-alert__title">
              {formatTranslation(t("trialtypes.changeTypeAlert"))}
            </span>
          </div>
          <p className="trial-type-change-alert__body">
            {formatTranslation(t("trialtypes.change.alert.message"))}
          </p>
        </div>
      )}{" "}
    </div>
  );
};

const TrialTypeCard = ({
  disabled,
  icon,
  iconDisabled,
  heading,
  description,
  selected,
  trialType,
  t,
}: TrialTypeCardTypes): ReactElement => {
  return (
    <Card
      data-testid={`trialType-${String(trialType)}`}
      hoverable={!disabled}
      className={`${
        !disabled && selected ? "active trialTypes-card" : "trialTypes-card"
      }`}
    >
      {disabled && <div className="backdrop"></div>}
      {disabled && (
        <LabelTag
          text={formatTranslation(t("trialtypes.label.comingSoon"))}
          type="disabled"
        />
      )}
      {/* <LabelTag type="Draft" className="disabled-label"  /> */}

      {!disabled && selected ? (
        <img
          className="trial-type-img"
          src={icon}
          alt={formatTranslation(t("trialtypes.trialTypeIcon"))}
        ></img>
      ) : (
        <img
          className="trial-type-img"
          src={iconDisabled}
          alt={formatTranslation(t("trialtypes.trialTypeDisabledIcon"))}
        ></img>
      )}

      {/* <SeedIcon selected = {selected} disabled={disabled} ></SeedIcon> */}

      <h2 className="trailTypes-card-heading">{heading.toUpperCase()}</h2>
      <p className="trailtype-card-description">{description}</p>
    </Card>
  );
};

export default TrialTypesComponent;
